import React, { useCallback, useReducer } from "react";
import { checkRole, reducerFn } from "../../helper";
import { useSelectRowsCtx } from "../shared/SelectRow";
import { Button, ButtonGroup } from "@shopify/polaris";
import AmazonIdeaPick from "./actions/amazon-idea-pick";
import AmazonIdeaAssign from "./actions/amazon-idea-assign";
import AmazonIdeaStart from "./actions/amazon-idea-start";
import AmazonIdeaDeny from "./actions/amazon-idea-deny";
import AmazonIdeaDelete from "./actions/amazon-idea-delete";
import AmazonIdeaReject from "./actions/amazon-idea-reject";
import AmazonIdeaAccept from "./actions/amazon-idea-accept";
import { useAppContext } from "../../context";
import AmazonIdeaUpdateKPI from "./actions/amazon-idea-update-kpi";
import { useAmazonIdeaCtx } from "./context";
import { get } from "lodash";
import { AMAZON_IDEA_STATUS } from "../../variable";
import AmazonIdeaMarkDone from "./actions/amazon-idea-mark-done";
import AmazonIdeaMarkDoneV2 from "./actions/amazon-idea-mark-done-v2";
import AmazonIdeaUpdateProductStatus from "./actions/amazon-idea-update-product-status";
import AmazonIdeaMarkBadgeModal from "./actions/amazon-idea-mark-badge-model";
import MarkAmazonIdeaStatusDone from "./actions/amazon-idea-mark-status-done";
import AmazonIdeaDuplication from "./actions/amazon-idea-duplication";
import AmazonIdeaUpdateStore from "./actions/amazin-idea-update-store";

export default function AmazonIdeaBulkAction() {
    const { currentUser } = useAppContext();
    const {
        isChannelLeader,
        isChannelIdea,
        isChannelSupporter,
        isChannelSeller,
        isSeller,
        isChannelSupportLeader,
    } = checkRole(currentUser);

    const { filter } = useAmazonIdeaCtx();

    const leader = isChannelSeller || isChannelLeader || isSeller;

    const [state, setState] = useReducer(reducerFn, {
        open: false,
        action: null,
    });

    const { ids, onCompleted } = useSelectRowsCtx();

    const toggleShowModal = useCallback((action) => {
        setState((p) => ({ ...p, open: !p.open, action }));
    }, []);

    const props = {
        open: state.open,
        toggleShowModal,
        onClose: toggleShowModal,
        onCompleted,
        ids,
    };

    const actionsMarkup = {
        assign: <AmazonIdeaAssign {...props} />,
        pick: <AmazonIdeaPick {...props} />,
        start: <AmazonIdeaStart {...props} />,
        markStatusDone: <MarkAmazonIdeaStatusDone {...props} />,
        deny: <AmazonIdeaDeny {...props} />,
        delete: <AmazonIdeaDelete {...props} />,
        reject: <AmazonIdeaReject {...props} />,
        updateKPI: <AmazonIdeaUpdateKPI {...props} />,
        accept: <AmazonIdeaAccept {...props} />,
        markDone: <AmazonIdeaMarkDoneV2 {...props} />,
        updateProductStatus: <AmazonIdeaUpdateProductStatus {...props} />,
        markBadge: <AmazonIdeaMarkBadgeModal {...props} />,
        clone: <AmazonIdeaDuplication {...props} />,
        updateStore: <AmazonIdeaUpdateStore {...props} />,
    };

    const isPartiallyCompleted = isStatusPartiallyCompleted(filter);
    const isDenied = isStatusDenied(filter);
    const isDoing = isStatusDoing(filter);
    const isDone = isStatusDone(filter);
    const isRjected = isStatusRjected(filter);

    return (
        <>
            <ButtonGroup segmented>
                {leader || isChannelSupportLeader ? (
                    <Button
                        children="Assign Amazon Idea"
                        onClick={() => toggleShowModal("assign")}
                        disclosure
                    />
                ) : null}
                {isChannelSupporter && (isPartiallyCompleted || isDoing) ? (
                    <Button
                        children="Deny Amazon Idea"
                        onClick={() => toggleShowModal("deny")}
                        disclosure
                    />
                ) : null}
                {isChannelSupporter && isPartiallyCompleted ? (
                    <Button
                        children="Start Amazon Idea"
                        onClick={() => toggleShowModal("start")}
                        disclosure
                    />
                ) : null}

                {isChannelSupporter && (isPartiallyCompleted || isDenied) ? (
                    <Button
                        children="Pick Amazon Idea"
                        onClick={() => toggleShowModal("pick")}
                        disclosure
                    />
                ) : null}
                {isChannelSupporter && isDoing ? (
                    <Button
                        children="Mark Done"
                        onClick={() => toggleShowModal("markDone")}
                        disclosure
                    />
                ) : null}
                {isChannelSupporter && (isPartiallyCompleted || isRjected) ? (
                    <Button
                        children="Mark Status Done"
                        onClick={() => toggleShowModal("markStatusDone")}
                        disclosure
                    />
                ) : null}

                {leader || isChannelSupportLeader ? (
                    <Button
                        children="Reject Amazon Idea"
                        onClick={() => toggleShowModal("reject")}
                        disclosure
                    />
                ) : null}

                {leader ? (
                    <Button
                        children="Accept Amazon Idea"
                        onClick={() => toggleShowModal("accept")}
                        disclosure
                    />
                ) : null}

                {leader || isChannelSupportLeader ? (
                    <Button
                        children="Update KPI"
                        onClick={() => toggleShowModal("updateKPI")}
                        disclosure
                    />
                ) : null}

                {isDone ? (
                    <Button
                        children="Update Product Status"
                        onClick={() => toggleShowModal("updateProductStatus")}
                        disclosure
                    />
                ) : null}

                <Button
                    children="Mark Badge"
                    onClick={() => toggleShowModal("markBadge")}
                    disclosure
                />

                {leader || isChannelIdea ? (
                    <Button
                        children="Clone"
                        onClick={() => toggleShowModal("clone")}
                        disclosure
                    />
                ) : null}

                {leader || isChannelIdea || isChannelSupportLeader || isChannelSupporter ? (
                    <Button
                        children="Update Store"
                        onClick={() => toggleShowModal("updateStore")}
                        disclosure
                    />
                ) : null}

                {leader || isChannelIdea ? (
                    <Button
                        children="Delete Amazon Idea"
                        onClick={() => toggleShowModal("delete")}
                        disclosure
                    />
                ) : null}
            </ButtonGroup>
            {actionsMarkup[state.action] || null}
        </>
    );
}

function isStatus(filter, target) {
    const status = get(filter, "status");

    if (!status) return true;

    return status === target;
}

function isStatusPartiallyCompleted(filter) {
    return isStatus(filter, AMAZON_IDEA_STATUS["Partially Completed"]);
}

function isStatusDenied(filter) {
    return isStatus(filter, AMAZON_IDEA_STATUS.Denied);
}

function isStatusDoing(filter) {
    return isStatus(filter, AMAZON_IDEA_STATUS.Doing);
}

function isStatusDone(filter) {
    return isStatus(filter, AMAZON_IDEA_STATUS.Done);
}

function isStatusRjected(filter) {
    return isStatus(filter, AMAZON_IDEA_STATUS.Rejected);
}
